import { defineStore } from "pinia";

import { errorDialogSeen } from "@/analytics/events/error";
import { trackEvent } from "@/analytics/track";
import { ApplicationError } from "@/error/ApplicationError";
import { errorMessage, errorToString } from "@/error/errorUnwrapper";
import { isNonPrimitive, isUnknownRecord } from "@/utils/type";
import type { UnknownRecord } from "@/utils/type";

export interface ErrorDetails {
  time: string;
  shortMessage: string;
  correlationId: string;
}

export const useGlobalErrorStore = defineStore("globalError", {
  state: () => ({
    globalError: null as { error: unknown; timestamp: number; message?: string } | null,
  }),
  actions: {
    setGlobalError(error: unknown, message?: string) {
      this.globalError = { error, timestamp: Date.now(), message };
      trackEvent(errorDialogSeen());
    },
  },
  getters: {
    errorDetails: (state): ErrorDetails | null => {
      if (!state.globalError) return null;

      const error = state.globalError.error;
      const appError = error instanceof ApplicationError ? error : null;

      // Extract the error message (can be nested in different places, depending on the shape of error)
      const shortMessage = errorToString(
        error,
        errorMessage(error, appError?.name || state.globalError.message || "Unknown"),
      );

      // Extract the correlation ID (it can also be in different places)
      const contexts = appError?.contexts || {};
      const correlationId = (contexts.request?.correlationId ||
        headers(contexts.cause?.config)?.["correlation-id"] ||
        "") as string;

      const time = new Date(state.globalError.timestamp).toISOString();

      return { time, shortMessage, correlationId };
    },
  },
});

function headers(config: unknown): UnknownRecord | undefined {
  return isNonPrimitive(config) && "headers" in config && isUnknownRecord(config.headers)
    ? config.headers
    : undefined;
}
